import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Add,
  Edit,
  Delete,
  Close,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

interface Role {
  id: string;
  name: string;
  is_system: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export default function RolesSettings() {
  const { access } = useAuth();
  const headers = { Authorization: `Bearer ${access}` };

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    display_order: 0,
  });

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles/', { headers });
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);
      setRoles(data);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setError('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setDialogMode('create');
    // Set display_order to next available (max + 10)
    const maxOrder = roles.length > 0
      ? Math.max(...roles.map(r => r.display_order))
      : 0;
    setFormData({
      name: '',
      display_order: maxOrder + 10,
    });
    setDialogOpen(true);
  };

  const handleEdit = (role: Role) => {
    setDialogMode('edit');
    setEditingRole(role);
    setFormData({
      name: role.name,
      display_order: role.display_order,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setError('Role name is required');
      return;
    }

    try {
      if (dialogMode === 'create') {
        await api.post('/roles/', formData, { headers });
      } else if (editingRole) {
        await api.patch(`/roles/${editingRole.id}/`, formData, { headers });
      }

      setDialogOpen(false);
      setEditingRole(null);
      setError('');
      await fetchRoles();
    } catch (err: any) {
      console.error('Failed to save role:', err);
      setError(err.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!roleToDelete) return;

    try {
      await api.delete(`/roles/${roleToDelete.id}/`, { headers });
      setDeleteConfirmOpen(false);
      setRoleToDelete(null);
      await fetchRoles();
    } catch (err: any) {
      console.error('Failed to delete role:', err);
      setError(err.response?.data?.detail || 'Failed to delete role');
      setDeleteConfirmOpen(false);
    }
  };

  const handleFormChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const columns: GridColDef<Role>[] = [
    {
      field: 'name',
      headerName: 'Role Name',
      flex: 2,
      minWidth: 200,
    },
    {
      field: 'display_order',
      headerName: 'Display Order',
      flex: 1,
      minWidth: 120,
      type: 'number',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            color="error"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">Team Roles</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage case team roles for your organization
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Add Role
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        System roles are created by default for all accounts. Lower display order values appear first in dropdowns.
      </Alert>

      <DataGrid
        rows={roles}
        columns={columns}
        loading={loading}
        autoHeight
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#616161',
            borderBottom: 'none',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#616161',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.95rem',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              '& .MuiIconButton-root': {
                color: 'white',
              },
            },
            '& .MuiDataGrid-sortIcon': {
              color: 'white',
            },
            '& .MuiDataGrid-menuIcon': {
              color: 'white',
            },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid',
            borderTopColor: 'divider',
          },
        }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add Team Role' : 'Edit Team Role'}
          <IconButton
            onClick={() => setDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Role Name"
              required
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              helperText="e.g., 'Outside Counsel', 'Local Counsel', 'Expert Witness'"
            />

            <TextField
              fullWidth
              label="Display Order"
              type="number"
              required
              value={formData.display_order}
              onChange={(e) => handleFormChange('display_order', parseInt(e.target.value) || 0)}
              helperText="Lower values appear first in dropdowns (e.g., 0 appears before 10)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Delete
          <IconButton
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {roleToDelete && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to delete this role? This action cannot be undone.
              </Alert>
              <Typography variant="body1">
                <strong>{roleToDelete.name}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Note: You cannot delete roles that are currently assigned to team members.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}